import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Select,
  Typography, Row, Col, message, Alert,
} from 'antd';
import {
  SendOutlined, ReloadOutlined, BellOutlined, GlobalOutlined,
  TeamOutlined, UserOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { pushApi, PushNotification, PushSendPayload } from '../../api/push';
import { PageHeader, StatCard, StatRow, DistrictSelect } from '../../components/shared';

const { Text } = Typography;
const { TextArea } = Input;

const TARGET_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  all: { label: 'Все', color: 'blue', icon: <GlobalOutlined /> },
  district: { label: 'Район', color: 'orange', icon: <TeamOutlined /> },
  user: { label: 'Пользователь', color: 'green', icon: <UserOutlined /> },
};

const PushCenter: React.FC = () => {
  const [history, setHistory] = useState<PushNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sendOpen, setSendOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [targetType, setTargetType] = useState<string>('all');
  const [form] = Form.useForm();

  const [messageApi, contextHolder] = message.useMessage();

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await pushApi.history({ page, size: 20 });
      const data = res.data;
      if (Array.isArray(data)) {
        setHistory(data);
        setTotal(data.length);
      } else {
        setHistory(data.items);
        setTotal(data.total);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleSend = async () => {
    let values: any;
    try { values = await form.validateFields(); } catch { return; }

    setSubmitting(true);
    try {
      const payload: PushSendPayload = {
        title: values.title,
        body: values.body,
        target_type: values.target_type,
        target_value: values.target_value,
      };
      await pushApi.send(payload);
      messageApi.success('Push-уведомление отправлено');
      setSendOpen(false);
      form.resetFields();
      fetchHistory();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка отправки');
    } finally {
      setSubmitting(false);
    }
  };

  const totalSent = history.reduce((s, p) => s + (p.sent_count || 0), 0);

  const columns: ColumnsType<PushNotification> = [
    {
      title: 'Заголовок',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title: string, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{title}</Text>
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>{record.body}</Text>
        </Space>
      ),
    },
    {
      title: 'Получатели',
      key: 'target',
      width: 180,
      render: (_: any, r: PushNotification) => {
        const cfg = TARGET_LABELS[r.target_type || 'all'] || TARGET_LABELS.all;
        return (
          <Space>
            <Tag icon={cfg.icon}>{cfg.label}</Tag>
            {r.target_value && <Text type="secondary" style={{ fontSize: 12 }}>{r.target_value}</Text>}
          </Space>
        );
      },
    },
    {
      title: 'Отправлено',
      dataIndex: 'sent_count',
      key: 'sent_count',
      width: 120,
      align: 'center',
      render: (c: number) => <Text strong>{c || 0}</Text>,
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (d: string) => d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '—',
    },
  ];

  return (
    <div>
      {contextHolder}

      <PageHeader
        title="Push-центр"
        subtitle="Отправка push-уведомлений жителям и история рассылок"
        extra={
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => { form.resetFields(); setTargetType('all'); setSendOpen(true); }}
           
            style={{ background: 'var(--l-accent)' }}
          >
            Отправить уведомление
          </Button>
        }
      />

      <StatRow span={8}>
        <StatCard label="Всего рассылок" value={total} icon={<BellOutlined />} color="var(--l-muted)" />
        <StatCard label="Доставлено уведомлений" value={totalSent} color="var(--l-accent)" colorValue />
        <StatCard
          label="Последняя рассылка"
          value={history[0] ? dayjs(history[0].created_at).format('DD.MM.YYYY') : '—'}
          color="var(--l-ok)"
        />
      </StatRow>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Card bodyStyle={{ padding: 0 }} extra={<Button icon={<ReloadOutlined />} onClick={fetchHistory}>Обновить</Button>}>
        <Table<PushNotification>
          rowKey="id"
          columns={columns}
          dataSource={history}
          loading={loading}
          pagination={{ current: page, pageSize: 20, total, onChange: p => setPage(p), showTotal: t => `Всего: ${t}` }}
          locale={{ emptyText: 'Уведомления не отправлялись' }}
          scroll={{ x: 700 }}
        />
      </Card>

      {/* Send Modal */}
      <Modal title="Отправить push-уведомление" open={sendOpen} onCancel={() => setSendOpen(false)} onOk={handleSend} confirmLoading={submitting} okText="Отправить" cancelText="Отмена" width={560} destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }} initialValues={{ target_type: 'all' }}>
          <Form.Item name="title" label="Заголовок" rules={[{ required: true, message: 'Введите заголовок' }]}>
            <Input placeholder="Внимание! Плановое отключение воды" maxLength={200} showCount />
          </Form.Item>
          <Form.Item name="body" label="Текст уведомления" rules={[{ required: true, message: 'Введите текст' }]}>
            <TextArea rows={3} placeholder="Подробности уведомления..." maxLength={1000} showCount />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="target_type" label="Получатели" rules={[{ required: true }]}>
                <Select onChange={(v: string) => setTargetType(v)}>
                  <Select.Option value="all"><Space><GlobalOutlined />Все пользователи</Space></Select.Option>
                  <Select.Option value="district"><Space><TeamOutlined />По району</Space></Select.Option>
                  <Select.Option value="user"><Space><UserOutlined />Конкретный пользователь</Space></Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              {targetType === 'district' && (
                <Form.Item name="target_value" label="Район" rules={[{ required: true, message: 'Выберите район' }]}>
                  <DistrictSelect placeholder="Выберите район" />
                </Form.Item>
              )}
              {targetType === 'user' && (
                <Form.Item name="target_value" label="ID пользователя" rules={[{ required: true, message: 'Введите ID' }]}>
                  <Input placeholder="UUID пользователя" />
                </Form.Item>
              )}
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default PushCenter;
